/**
 * Letter-first onboarding — review Orion's result.
 *
 * The draft (or outline, or polish) is editable text, never a locked
 * result. "Use this" writes it into the letter and returns to the canvas;
 * "Keep my version" discards it. The user can edit right here first.
 */
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  TextInput,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { letterToLovedOnesService } from '../../services/letterToLovedOnesInfo';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('LetterReview');

type ReviewRouteProp = RouteProp<
  {
    params: {
      letterId: string;
      content: string;
      mode: 'improve' | 'draft' | 'outline';
    };
  },
  'params'
>;

const HEADINGS = {
  improve: 'Polished — same voice, tightened',
  draft: 'Your draft',
  outline: 'Your outline',
};

export const LetterReviewScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<ReviewRouteProp>();
  const { letterId, content: initialContent, mode } = route.params;

  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);

  const handleUse = async () => {
    if (saving) return;
    try {
      setSaving(true);
      await letterToLovedOnesService.updateLetterToLovedOnes(letterId, {
        letterContent: content,
      });
      navigation.navigate('LetterCanvas', { letterId, updatedContent: content });
    } catch (error) {
      logger.error('Failed to apply result:', error);
      Alert.alert('Error', 'Failed to update your letter. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{HEADINGS[mode]}</Text>
        <View style={styles.headerSpacer} />
      </View>

      <KeyboardAvoidingView
        style={styles.sheet}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <Text style={styles.note}>
          Every line is yours to change — edit right here.
        </Text>
        <TextInput
          style={styles.editor}
          multiline
          textAlignVertical="top"
          value={content}
          onChangeText={setContent}
          accessibilityLabel="Orion's result, editable"
        />

        <TouchableOpacity
          style={[styles.useButton, saving && styles.disabled]}
          onPress={handleUse}
          disabled={saving}
          accessibilityRole="button"
          accessibilityLabel={mode === 'outline' ? 'Write from this outline' : 'Use this letter'}
          accessibilityState={{ disabled: saving }}
        >
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.useButtonText}>
              {mode === 'outline' ? 'Write from this outline' : 'Use this letter'}
            </Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.keepButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Keep my version"
        >
          <Text style={styles.keepButtonText}>Keep my version</Text>
        </TouchableOpacity>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(16),
    paddingHorizontal: ms(16),
    paddingBottom: ms(16),
  },
  note: {
    fontSize: ms(13, 0.3),
    color: '#6B7280',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  editor: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(15),
    fontSize: ms(16, 0.3),
    lineHeight: ms(24, 0.3),
    color: '#2C4364',
    marginBottom: ms(14),
  },
  useButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
  },
  useButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  keepButton: {
    alignItems: 'center',
    paddingVertical: ms(13),
  },
  keepButtonText: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#6B7280',
  },
  disabled: {
    opacity: 0.5,
  },
});

export default LetterReviewScreen;
